import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Check, Sparkles, Gift, Loader2 } from 'lucide-react';
import { useUser } from '@/contexts/UserContext';
import { billingService } from '@/services/billingService';
import CheckoutForm from '@/components/billing/CheckoutForm';
import { toast } from 'sonner';

interface Plan {
  id: string;
  name: string;
  description?: string;
  price: number;
  priceId?: string;
  stripePriceId?: string;
  interval: 'month' | 'year';
  features: string[];
  recommended?: boolean;
}

export default function Pricing() {
  const { user } = useUser();
  const navigate = useNavigate();
  const [selectedPlan, setSelectedPlan] = useState<string | null>(null);
  const [voucherCode, setVoucherCode] = useState("");
  const [showCheckout, setShowCheckout] = useState(false);
  const [plans, setPlans] = useState<Plan[]>([]);
  const [isLoadingPlans, setIsLoadingPlans] = useState(true);

  useEffect(() => {
    const fetchPlans = async () => {
      try {
        setIsLoadingPlans(true);
        const backendPlans = await billingService.getPlans();
        if (backendPlans && Array.isArray(backendPlans)) {
          // Filter out free plan and transform to match expected format
          const paidPlans = backendPlans
            .filter((p: any) => p.id !== 'free')
            .map((p: any) => ({
              id: p.id,
              name: p.name,
              description: p.description,
              price: p.amount ? p.amount / 100 : p.price || 0,
              priceId: p.priceId || p.stripePriceId,
              stripePriceId: p.priceId || p.stripePriceId,
              interval: p.interval || 'month',
              features: p.features || [],
              recommended: p.id === 'solo',
            }));
          setPlans(paidPlans);
        }
      } catch (error) {
        console.error('Failed to fetch plans:', error);
        toast.error('Failed to load pricing plans');
      } finally {
        setIsLoadingPlans(false);
      }
    };
    fetchPlans();
  }, []);

  const handleSelectPlan = async (plan: Plan) => {
    try {
      // Check if user is logged in
      if (!user) {
        // Store selected plan and redirect to signup
        localStorage.setItem('selectedPlan', plan.id);
        localStorage.setItem('voucherCode', voucherCode);
        navigate('/');
        return;
      }

      // All plans require payment
      if (plan.stripePriceId || plan.priceId) {
        setSelectedPlan(plan.id);
        setShowCheckout(true);
      } else {
        // Invalid plan
        toast.error('Invalid plan selected');
      }
    } catch (error) {
      console.error('Error selecting plan:', error);
      toast.error('Failed to start checkout process');
    }
  };

  const handleCheckoutSuccess = () => {
    setShowCheckout(false);
    navigate('/payment/return');
  };

  const handleCheckoutCancel = () => {
    setShowCheckout(false);
    setSelectedPlan(null);
  };

  if (showCheckout && selectedPlan) {
    const plan = plans.find(p => p.id === selectedPlan);
    if (!plan || (!plan.stripePriceId && !plan.priceId)) {
      return <div>Plan not found</div>;
    }

    return (
      <div className="min-h-screen bg-gradient-to-b from-white to-gray-50">
        <div className="container mx-auto px-4 py-16 max-w-4xl">
          <div className="mb-6 text-center">
            <h1 className="text-3xl font-bold mb-2">Complete Your {plan.name} Subscription</h1>
            <p className="text-xl text-muted-foreground">
              You're upgrading to {plan.name} for ${plan.price}/{plan.interval} with 14-day free trial
            </p>
          </div>
          
          <CheckoutForm
            priceId={plan.stripePriceId || plan.priceId || ''}
            planName={plan.name}
            voucherCode={voucherCode || undefined}
            onSuccess={handleCheckoutSuccess}
            onCancel={handleCheckoutCancel}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-gray-50">
      <div className="container mx-auto px-4 py-16">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold mb-4">Choose Your Plan</h1>
          <p className="text-xl text-muted-foreground">
            All plans include a 14-day free trial. Cancel anytime.
          </p>
        </div>

        {/* Voucher Code Input */}
        <div className="max-w-md mx-auto mb-8">
          <div className="space-y-2">
            <Label htmlFor="voucher" className="flex items-center gap-2">
              <Gift className="h-4 w-4" />
              Have a voucher code?
            </Label>
            <Input
              id="voucher"
              placeholder="Enter code (e.g., BETA2025)"
              value={voucherCode}
              onChange={(e) => setVoucherCode(e.target.value.toUpperCase())}
              className="font-mono"
            />
            <p className="text-xs text-muted-foreground">
              Enter your voucher code before selecting a plan
            </p>
          </div>
        </div>

        {isLoadingPlans ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        ) : (
          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {plans.map((plan) => (
            <Card 
              key={plan.id} 
              className={`relative ${plan.recommended ? 'border-primary shadow-lg' : ''}`}
            >
              {plan.recommended && (
                <div className="absolute -top-4 left-1/2 -translate-x-1/2">
                  <Badge className="px-3 py-1">
                    <Sparkles className="h-3 w-3 mr-1" />
                    Recommended
                  </Badge>
                </div>
              )}
              
              <CardHeader>
                <CardTitle className="text-2xl">{plan.name}</CardTitle>
                <CardDescription>
                  <div className="text-3xl font-bold mt-2">
                    ${plan.price}
                    <span className="text-base font-normal text-muted-foreground">
                      /{plan.interval}
                    </span>
                  </div>
                </CardDescription>
              </CardHeader>
              
              <CardContent className="space-y-4">
                <ul className="space-y-3">
                  {plan.features.map((feature, index) => (
                    <li key={index} className="flex items-start gap-2">
                      <Check className="h-5 w-5 text-primary mt-0.5 flex-shrink-0" />
                      <span className="text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>
                
                {voucherCode && plan.price > 0 && (
                  <div className="p-3 bg-green-50 rounded-lg border border-green-200">
                    <div className="flex items-center gap-2">
                      <Gift className="h-4 w-4 text-green-600" />
                      <span className="text-sm font-medium text-green-800">
                        Voucher code will be applied at checkout
                      </span>
                    </div>
                  </div>
                )}
              </CardContent>
              
              <CardFooter>
                <Button 
                  className="w-full" 
                  variant={plan.recommended ? 'default' : 'outline'}
                  onClick={() => handleSelectPlan(plan)}
                >
                  {plan.price === 0 ? (
                    user ? 'Current Plan' : 'Start Free'
                  ) : (
                    `Choose ${plan.name}`
                  )}
                </Button>
              </CardFooter>
            </Card>
            ))}
          </div>
        )}

        <div className="text-center mt-12 space-y-4">
          <p className="text-muted-foreground">
            All plans include a 14-day free trial. No credit card required.
          </p>
          <div className="flex items-center justify-center gap-4 text-sm text-muted-foreground">
            <span>✓ Cancel anytime</span>
            <span>✓ No hidden fees</span>
            <span>✓ Secure payment</span>
          </div>
        </div>

        {/* FAQ Section */}
        <div className="mt-16 max-w-3xl mx-auto">
          <h2 className="text-2xl font-bold text-center mb-8">Frequently Asked Questions</h2>
          <div className="space-y-6">
            <div>
              <h3 className="font-semibold mb-2">Can I change plans later?</h3>
              <p className="text-muted-foreground">
                Yes! You can upgrade or downgrade your plan at any time. Changes take effect immediately.
              </p>
            </div>
            <div>
              <h3 className="font-semibold mb-2">What payment methods do you accept?</h3>
              <p className="text-muted-foreground">
                We accept all major credit cards, debit cards, and support international payments through Stripe.
              </p>
            </div>
            <div>
              <h3 className="font-semibold mb-2">Is there a setup fee?</h3>
              <p className="text-muted-foreground">
                No setup fees! You only pay the monthly subscription price.
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}